import jwt_decode from "jwt-decode";
import { refreshAuthToken, forgotPassword as forgotPasswordRequest } from "../api/cognitoOnboarding";
import { getProviderData } from "../actions";
import { saveusercredentials } from "../redux/actions/auth.action";
import { store } from "../redux/store";
import {
   buildStsTokenManager,
   forceReauth,
   getNorthwellUser,
   getStsTokens,
   getUserEmail,
   saveAuthSession,
   saveStsTokens,
} from "./session";

async function triggerLogout() {
   const { logout } = await import("../Apimanager/Networking");
   return logout();
}

export async function establishSession(success = {}) {
   const accessToken = success.accessToken || success.cognitoAccessToken;
   const refreshToken = success.refreshToken || success.cognitoRefreshToken;
   const idToken = success.idToken || success.cognitoIdToken;

   if (!accessToken || !refreshToken) {
      throw new Error("Auth session is missing access or refresh token from backend");
   }

   const stsTokenManager = buildStsTokenManager({
      accessToken,
      refreshToken,
      idToken,
      expiresIn: success.expiresIn,
      username: success.username || success.userData?.email,
   });

   saveAuthSession({
      stsTokenManager,
      userData: success.userData || success.user,
   });

   return stsTokenManager;
}

export async function refreshSession() {
   const stsTokens = getStsTokens();
   const refreshToken = stsTokens?.refreshToken;
   const email = getUserEmail();
   if (!refreshToken) {
      forceReauth();
      return;
   }

   try {
      const response = await refreshAuthToken({ refreshToken, email });
      const stsTokenManager = buildStsTokenManager({
         accessToken: response.accessToken,
         refreshToken: response.refreshToken || refreshToken,
         idToken: response.idToken || stsTokens.idToken,
         expiresIn: response.expiresIn,
         username: stsTokens.username,
      });

      return stsTokenManager;
   } catch (error) {
      console.log("Backend auth refresh error", error);
      forceReauth();
   }
}

export async function getAccessToken() {
   const northwelluser = getNorthwellUser();
   let stsTokens = getStsTokens();
   if (stsTokens) {
      let accessTokenExpTime;

      try {
         accessTokenExpTime = jwt_decode(stsTokens.idToken).exp;
      } catch (error) {
         await triggerLogout();
         return;
      }

      if (typeof accessTokenExpTime !== "number" || Number.isNaN(accessTokenExpTime)) {
         await triggerLogout();
         return;
      }

      const now = parseInt(Date.now() / 1000, 10);

      if (now >= accessTokenExpTime - 30) {
         const newStsTokens = await refreshSession();

         if (!newStsTokens || !newStsTokens.idToken) {
            await triggerLogout();
            return;
         }

         saveStsTokens(newStsTokens);
         return newStsTokens.idToken;
      }

      return stsTokens.idToken;
   }

   if (northwelluser && stsTokens === undefined) {
      await triggerLogout();
      return;
   }

   if (stsTokens !== null && stsTokens !== undefined) {
      await triggerLogout();
   }
}

export async function signOut() {
   // Session cleanup is handled by Networking.logout; no client Cognito SDK call.
}

export async function resetPassword(emailAddress, success = () => { }, onError = () => { }) {
   try {
      await forgotPasswordRequest({ email: emailAddress });
      success();
   } catch (error) {
      onError(error);
   }
}

export function parseGoogleCallbackTokens(search, hash) {
   const queryParams = new URLSearchParams(search);
   const error = queryParams.get("error_description") || queryParams.get("error");

   const hashParams = new URLSearchParams((hash || "").replace(/^#/, ""));

   return {
      error,
      accessToken: hashParams.get("accessToken") || hashParams.get("cognitoAccessToken"),
      refreshToken: hashParams.get("refreshToken") || hashParams.get("cognitoRefreshToken"),
      idToken: hashParams.get("idToken") || hashParams.get("cognitoIdToken"),
   };
}

export function completeGoogleAuthLogin(search, hash, onSuccess = () => { }, onError = () => { }) {
   const { error, accessToken, refreshToken, idToken } = parseGoogleCallbackTokens(search, hash);

   if (error) {
      onError(new Error(error));
      return;
   }

   if (!accessToken || !refreshToken) {
      onError(new Error("Login session is missing tokens. Please try again."));
      return;
   }

   establishSession({ accessToken, refreshToken, idToken })
      .then(() => {
         getProviderData(null, false, (providerDetails) => {
            if (!providerDetails?.id) {
               onError(new Error("Unable to load provider profile"));
               return;
            }

            store.dispatch(
               saveusercredentials({
                  user: {
                     id: providerDetails.id,
                     userId: providerDetails.id,
                     email: providerDetails.email,
                     name: providerDetails.name,
                     role: providerDetails.role,
                     playbackAIOnly: providerDetails.playbackAIOnly,
                     isTrialUser: providerDetails.isTrialUser,
                     isSSOLogin: providerDetails.isSSOLogin,
                  },
               })
            );

            onSuccess(providerDetails);
         });
      })
      .catch(onError);
}
